import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentOrg } from "@/hooks/use-current-org";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Users, Megaphone, Layers, Send } from "lucide-react";
import { OrgSwitcher } from "@/components/org-switcher";

export const Route = createFileRoute("/_authenticated/dashboard")({
  component: Dashboard,
});

function Dashboard() {
  const { orgId } = useCurrentOrg();
  const [stats, setStats] = useState({ contacts: 0, segments: 0, campaigns: 0, sent: 0 });

  useEffect(() => {
    if (!orgId) return;
    (async () => {
      const [c, s, ca, r] = await Promise.all([
        supabase.from("contacts").select("id", { count: "exact", head: true }).eq("organization_id", orgId),
        supabase.from("segments").select("id", { count: "exact", head: true }).eq("organization_id", orgId),
        supabase.from("campaigns").select("id", { count: "exact", head: true }).eq("organization_id", orgId),
        supabase.from("campaign_recipients").select("id", { count: "exact", head: true }).eq("organization_id", orgId).eq("status", "sent"),
      ]);
      setStats({ contacts: c.count ?? 0, segments: s.count ?? 0, campaigns: ca.count ?? 0, sent: r.count ?? 0 });
    })();
  }, [orgId]);

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground text-sm mt-1">Overview of your campaign activity.</p>
        </div>
        <OrgSwitcher />
      </div>

      {!orgId ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            No organization yet. Create one from the Organizations page to get started.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat icon={Users} label="Contacts" value={stats.contacts} />
          <Stat icon={Layers} label="Segments" value={stats.segments} />
          <Stat icon={Megaphone} label="Campaigns" value={stats.campaigns} />
          <Stat icon={Send} label="Messages sent" value={stats.sent} />
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value }: { icon: any; label: string; value: number }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
        <Icon className="h-4 w-4 text-primary" />
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-bold">{value.toLocaleString()}</div>
      </CardContent>
    </Card>
  );
}
